/**
 * spark-esign over http, at the URL `source.ts` reads. The contract is spark-esign's
 * `docs/api-contract.md`; every type and guard comes from `contract.ts`.
 *
 * The base is `window.config.esignApiUrl`, read on every call. Every call carries the signed-in
 * owner's access token raw in `Authorization` (no `Bearer`), read from the store on every call so a
 * refreshed token is used without a reload, as the Mailbox and blog clients do. These calls use
 * `fetch`, not axios, on purpose: the global refresh-and-teardown interceptor
 * (`src/utils/auth-interceptor.ts`) never sees them, so a spark-esign `401` or `403` never logs the
 * owner out. The section shows its own copy for them instead.
 *
 * Every non-2xx rejects with a {@link ContractError}: the service's own body when it is one (only its
 * contract fields travel), otherwise one read from the status alone, because a gateway can answer
 * with no service body at all (the Mailbox rule). No URL configured, the network, or a 2xx body that is
 * not a status reject with a plain `Error`, which the section shows as its generic line. Bodies are
 * never logged.
 */
import { store } from '@/store';

import {
  CONTRACT_TYPE,
  isContractError,
  toContractStatus,
  type ContractDocument,
  type ContractError,
  type ContractStatus,
  type StartBody,
} from './contract';
import { esignApiUrl } from './source';

type Method = 'GET' | 'POST';

/** The owner's access token, read from the store on EVERY call; `null` when nobody is signed in. */
const ownerToken = (): string | null => {
  const token = store.getState().authentication.access_token;
  return typeof token === 'string' && token !== '' ? token : null;
};

const contractPath = (brandId: string): string => `/brands/${encodeURIComponent(brandId)}/contracts/${CONTRACT_TYPE}`;

/** The body as JSON, or `null` when there is none or it is not JSON (a gateway's HTML page). */
const readJson = async (response: Response): Promise<unknown> => {
  try {
    return await response.json();
  } catch {
    return null;
  }
};

/** A refusal: the service's error, copied field by field so nothing else from the body travels with
 *  it, or, when the body is not one, the error its status stands for. */
const toContractError = (status: number, payload: unknown): ContractError => {
  if (isContractError(payload)) {
    return { code: payload.code, message: payload.message, retry_after: payload.retry_after, fields: payload.fields };
  }
  const code =
    status === 401 ? 'UNAUTHORIZED' : status === 403 ? 'FORBIDDEN' : status === 404 ? 'NOT_FOUND' : 'INTERNAL';
  return { code, message: '', retry_after: null, fields: null };
};

/**
 * One call, answered 2xx, or a rejection. With no URL configured or no owner token there is no call
 * at all: the first rejects with a plain `Error`, the second as `UNAUTHORIZED`, the same as the
 * service's `401`.
 */
const send = async (method: Method, path: string, accept: string, body?: StartBody): Promise<Response> => {
  const base = esignApiUrl();
  if (base === null) throw new Error('spark-esign: not configured');
  const token = ownerToken();
  if (token === null) {
    throw { code: 'UNAUTHORIZED', message: '', retry_after: null, fields: null } satisfies ContractError;
  }
  const response = await fetch(`${base}${path}`, {
    method,
    headers: { Accept: accept, Authorization: token, ...(body ? { 'Content-Type': 'application/json' } : {}) },
    ...(body ? { body: JSON.stringify(body) } : {}),
  });
  if (!response.ok) throw toContractError(response.status, await readJson(response));
  return response;
};

/** A JSON call: resolves with the parsed body. */
const request = async (method: Method, path: string, body?: StartBody): Promise<unknown> =>
  readJson(await send(method, path, 'application/json', body));

/** `GET /brands/{brand_id}/contracts/{type}`. */
export const fetchStatus = async (brandId: string): Promise<ContractStatus> =>
  toContractStatus(await request('GET', contractPath(brandId)));

/** `POST /brands/{brand_id}/contracts/{type}` (Start): answers `201` with the status, now `sent`. */
export const start = async (brandId: string, body: StartBody): Promise<ContractStatus> =>
  toContractStatus(await request('POST', contractPath(brandId), body));

/** `POST .../resend`: DocuSign sends the same envelope again. No body. */
export const resend = async (brandId: string): Promise<ContractStatus> =>
  toContractStatus(await request('POST', `${contractPath(brandId)}/resend`));

/** `POST .../restart` (Start over): voids the pending envelope, then sends a new one. */
export const restart = async (brandId: string, body: StartBody): Promise<ContractStatus> =>
  toContractStatus(await request('POST', `${contractPath(brandId)}/restart`, body));

/** The filename a `Content-Disposition` header names: the RFC 5987 `filename*` form first (decoded),
 *  then the plain `filename`, quoted or not. `null` when the header is absent or names none. */
export const filenameOf = (header: string | null): string | null => {
  if (header === null) return null;
  const extended = /filename\*\s*=\s*[^']*'[^']*'([^;]+)/i.exec(header);
  if (extended) {
    try {
      const decoded = decodeURIComponent(extended[1].trim());
      if (decoded !== '') return decoded;
    } catch {
      // A malformed percent escape: fall back to the plain form, if any.
    }
  }
  const plain = /(?:^|;)\s*filename\s*=\s*(?:"([^"]*)"|([^;]*))/i.exec(header);
  const name = (plain?.[1] ?? plain?.[2] ?? '').trim();
  return name === '' ? null : name;
};

/** `GET .../document`: the signed declaration as a blob, with the filename its header names. */
export const download = async (brandId: string): Promise<ContractDocument> => {
  const response = await send('GET', `${contractPath(brandId)}/document`, 'application/pdf');
  return { blob: await response.blob(), filename: filenameOf(response.headers.get('content-disposition')) };
};
